import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, Send, Paperclip, X, Download, MessageSquare, Filter, Search, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LoadingQuip from "@/components/LoadingQuip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

// Thumbnail Component for Question Cards
function MediaThumbnail({ attachment, getAuthenticatedFileUrl }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  
  const isImage = attachment.file_type === 'image' || 
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachment.filename);
  const isVideo = attachment.file_type === 'video' || 
    /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment.filename);
  
  useEffect(() => {
    if (isImage || isVideo) {
      getAuthenticatedFileUrl(attachment.file_path).then(url => {
        setThumbnailUrl(url);
      });
    }
  }, [attachment.file_path, isImage, isVideo, getAuthenticatedFileUrl]);
  
  if (!thumbnailUrl) return null;
  
  return (
    <div className="relative w-16 h-16 rounded overflow-hidden border border-gray-200 flex-shrink-0">
      {isImage ? (
        <img
          src={thumbnailUrl}
          alt="thumbnail"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
        </div>
      )}
    </div>
  );
}

// File Preview Component for Students
function FilePreviewCard({ attachment, getAuthenticatedFileUrl, downloadFile, formatFileSize, isDarkMode, colors }) {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);
  
  const isImage = attachment.file_type === 'image' || 
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachment.filename);
  const isVideo = attachment.file_type === 'video' || 
    /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment.filename);
  const isPdf = attachment.file_type === 'pdf' || 
    /\.pdf$/i.test(attachment.filename);
  
  // Auto-load media when component mounts
  useEffect(() => {
    let isMounted = true;
    
    if (isImage || isVideo || isPdf) {
      console.log('[FilePreviewCard] Loading file:', attachment.file_path);
      getAuthenticatedFileUrl(attachment.file_path).then(url => {
        console.log('[FilePreviewCard] Got URL:', url);
        if (isMounted) {
          setFileUrl(url);
          setLoading(false);
        }
      }).catch(err => {
        console.error('[FilePreviewCard] Error loading:', err);
        if (isMounted) {
          setFileUrl(null);
          setLoading(false);
        }
      });
    } else {
      setLoading(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [attachment.file_path]);
  
  const cardBg = isDarkMode ? '#0f2744' : 'white';
  const headerBg = isDarkMode ? '#1e3a5f' : '#f9fafb';
  const textColor = isDarkMode ? '#ffffff' : '#111827';
  const subtextColor = isDarkMode ? '#94a3b8' : '#6b7280';
  const borderColor = isDarkMode ? '#1e3a5f' : '#e5e7eb';
  
  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
      {/* File Header */}
      <div className="flex items-center justify-between p-3 border-b" style={{ backgroundColor: headerBg, borderColor: borderColor }}>
        <div className="flex items-center space-x-3">
          <Paperclip className="h-4 w-4 flex-shrink-0" style={{ color: subtextColor }} />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: textColor }}>{attachment.filename}</p>
            <p className="text-xs" style={{ color: subtextColor }}>
              {formatFileSize(attachment.file_size)} • {attachment.file_type}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => downloadFile(attachment.file_path, attachment.filename)}
          className="flex-shrink-0"
        >
          <Download className="h-4 w-4" style={{ color: isDarkMode ? '#60a5fa' : undefined }} />
        </Button>
      </div>
      
      {/* File Preview */}
      <div className="p-3" style={{ backgroundColor: headerBg }}>
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingQuip size="sm" />
          </div>
        ) : fileUrl ? (
          <>
            {isImage && (
              <div className="flex justify-center">
                <img
                  src={fileUrl}
                  alt={attachment.filename}
                  className="max-w-full h-auto rounded border border-gray-300 shadow-sm"
                  style={{ maxHeight: '500px' }}
                />
              </div>
            )}
            {isVideo && (
              <video
                key={fileUrl}
                src={fileUrl}
                controls
                className="w-full h-auto rounded border border-gray-300 shadow-sm"
                style={{ maxHeight: '500px' }}
              >
                Your browser does not support video playback.
              </video>
            )}
            {isPdf && (
              <div className="space-y-3">
                {/* PDF Thumbnail Preview */}
                {fileUrl && (
                  <div 
                    className="relative bg-white rounded border-2 border-gray-200 overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => setShowPdfModal(true)}
                    style={{ height: '500px' }}
                  >
                    <embed
                      src={`${fileUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                      type="application/pdf"
                      className="w-full h-full pointer-events-none"
                    />
                    
                    {/* Overlay to make entire area clickable */}
                    <div 
                      className="absolute inset-0 cursor-pointer"
                      onClick={() => setShowPdfModal(true)}
                      title="Click to view full PDF"
                    />
                  </div>
                )}
                
                {/* View PDF Button */}
                <Button
                  onClick={() => setShowPdfModal(true)}
                  className="w-full"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Full PDF
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Paperclip className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p className="text-sm font-medium">Unable to load media</p>
            <p className="text-xs mt-1">Please try downloading the file</p>
          </div>
        )}
      </div>
      
      {/* PDF Viewer Modal */}
      {isPdf && showPdfModal && fileUrl && (
        <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
          <DialogContent className="max-w-5xl h-[90vh] overflow-hidden p-0">
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle>{attachment.filename}</DialogTitle>
            </DialogHeader>
            <div className="w-full h-full px-6 pb-6" style={{ height: 'calc(90vh - 80px)' }}>
              <object
                data={fileUrl}
                type="application/pdf"
                className="w-full h-full rounded border border-gray-300"
              >
                <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500">
                  <p className="font-medium mb-4">Unable to display PDF in browser.</p>
                  <Button
                    onClick={() => downloadFile(attachment.file_path, attachment.filename)}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF Instead
                  </Button>
                </div>
              </object>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function CurbsideConsult({ user, logout }) {
  const [activeView, setActiveView] = useState('submit'); // 'submit', 'browse', or 'archive'
  const [questionText, setQuestionText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  // Theme-aware colors - SIMPLE RULE:
  // Dark background = White text (#ffffff)
  // White background = Black text (#000000)
  const colors = {
    heading: isDarkMode ? '#ffffff' : '#000000',
    subtext: isDarkMode ? '#ffffff' : '#000000',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    mainBg: isDarkMode ? '#092036' : '#F4F4F4',
    headerBg: isDarkMode ? '#092036' : 'white',
    border: isDarkMode ? '#1e3a5f' : '#e5e7eb',
    buttonOutline: isDarkMode ? '#60a5fa' : undefined,
    buttonOutlineText: isDarkMode ? '#60a5fa' : undefined,
  };
  
  const token = localStorage.getItem("session_token");
  const MAX_FILES = 3;
  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB for images/videos
  const [fileUrls, setFileUrls] = useState({});

  // Handle URL parameters (from email links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const questionId = urlParams.get('question');
    
    if (questionId) {
      // Switch to browse view
      setActiveView('browse');
      
      // Fetch questions and open the specific one
      fetchQuestions().then(() => {
        // Small delay to ensure questions are loaded
        setTimeout(() => {
          viewQuestionDetail(questionId);
        }, 500);
      });
    }
  }, []);

  useEffect(() => {
    if (activeView === 'browse' || activeView === 'archive') {
      fetchQuestions();
    }
  }, [activeView]);

  // Fetch authenticated file and create blob URL
  const getAuthenticatedFileUrl = useCallback(async (filePath) => {
    console.log('[getAuthenticatedFileUrl] START - Checking cache for:', filePath);
    
    if (fileUrls[filePath]) {
      console.log('[getAuthenticatedFileUrl] CACHED - Returning existing URL');
      return fileUrls[filePath];
    }

    try {
      const pathParts = filePath.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const subfolder = pathParts[pathParts.length - 2];
      
      // Get fresh token for each request
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      
      const apiUrl = `${API}/curbside-consult/files/${subfolder}/${fileName}`;
      
      console.log('[getAuthenticatedFileUrl] FETCHING:', {
        filePath,
        subfolder,
        fileName,
        apiUrl,
        hasToken: !!currentToken,
        tokenPrefix: currentToken ? currentToken.substring(0, 10) + '...' : 'NO TOKEN'
      });
      
      const response = await axios.get(apiUrl, {
        headers,
        responseType: 'blob'
      });
      
      console.log('[getAuthenticatedFileUrl] SUCCESS - File fetched:', {
        size: response.data.size,
        type: response.data.type,
        status: response.status
      });
      
      if (!response.data || response.data.size === 0) {
        console.error('[getAuthenticatedFileUrl] ERROR: Empty response data');
        return null;
      }
      
      const blobUrl = URL.createObjectURL(response.data);
      console.log('[getAuthenticatedFileUrl] BLOB URL created:', blobUrl);
      
      setFileUrls(prev => ({ ...prev, [filePath]: blobUrl }));
      return blobUrl;
    } catch (error) {
      console.error("[getAuthenticatedFileUrl] ERROR - CAUGHT:", {
        filePath,
        errorMessage: error.message,
        errorData: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        config: error.config
      });
      
      return null;
    }
  }, [fileUrls]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(`${API}/curbside-consult/questions`, { headers });
      setQuestions(response.data);
    } catch (error) {
      toast.error("Error loading questions");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Check file count
    if (files.length + selectedFiles.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }
    
    // Check file types (only images and videos)
    const invalidFiles = selectedFiles.filter(file => {
      return !file.type.startsWith('image/') && !file.type.startsWith('video/');
    });
    if (invalidFiles.length > 0) {
      toast.error(`Only images and videos allowed. ${invalidFiles[0].name} is not supported.`);
      return;
    }
    
    // Check file sizes
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error(`Files must be less than 500MB. ${oversizedFiles[0].name} is too large.`);
      return;
    }
    
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (questionText.trim().length < 10) {
      toast.error("Question must be at least 10 characters");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('question_text', questionText);
      formData.append('display_name', displayName || 'Anonymous');
      
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const currentToken = localStorage.getItem("session_token");
      const response = await axios.post(
        `${API}/curbside-consult/submit`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(response.data.message || "Question submitted successfully!");
      
      // Reset form
      setQuestionText('');
      setDisplayName('');
      setFiles([]);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  const viewQuestionDetail = async (questionId) => {
    try {
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(
        `${API}/curbside-consult/question/${questionId}`,
        { headers }
      );
      setSelectedQuestion(response.data);
    } catch (error) {
      toast.error("Error loading question details");
    }
  };

  const downloadFile = async (filePath, filename) => {
    try {
      // Extract subfolder and filename from path
      const pathParts = filePath.split('/');
      const file = pathParts[pathParts.length - 1];
      const subfolder = pathParts[pathParts.length - 2];
      
      const currentToken = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${currentToken}` };
      const response = await axios.get(
        `${API}/curbside-consult/files/${subfolder}/${file}`,
        {
          headers,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded");
    } catch (error) {
      toast.error("Error downloading file");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get all unique tags from questions
  const allTags = [...new Set(questions.flatMap(q => q.tags || []))];

  // Filter questions based on view
  // Filter questions by search and tags first
  const searchFilteredQuestions = questions.filter(q => {
    const matchesSearch = searchTerm === '' || 
      q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.admin_response_text?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(tag => q.tags?.includes(tag));
    
    return matchesSearch && matchesTags;
  });

  // Separate archived and non-archived questions
  const archivedQuestions = searchFilteredQuestions.filter(q => q.status === 'archived');
  const nonArchivedQuestions = searchFilteredQuestions.filter(q => q.status !== 'archived');
  
  // For browse view, show 10 most recent non-archived questions
  const recentQuestions = nonArchivedQuestions.slice(0, 10);
  
  // Select which questions to display based on active view
  const displayedQuestions = activeView === 'browse' ? recentQuestions : archivedQuestions;

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <div className="min-h-screen grid-layout-page" style={{ background: colors.mainBg }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white shadow-sm'}`} style={{ backgroundColor: colors.headerBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <img src={LOGO_URL} alt="Logo" className="h-8 sm:h-10 w-auto cursor-pointer flex-shrink-0" onClick={() => navigate('/dashboard')} style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <h1 className="text-base sm:text-2xl font-bold truncate" style={{ color: colors.heading }}>Curbside Consult</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <DarkModeToggle />
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-xs sm:text-sm px-2 sm:px-3" style={{ color: colors.subtext }}>
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <Button variant="ghost" onClick={logout} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3">
                <LogOut className="h-4 w-4" style={{ color: '#9E3639' }} />
                <span className="hidden sm:inline" style={{ color: colors.subtext }}>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pt-8">
        {/* View Toggle */}
        <div className="flex space-x-2 mb-6">
          <Button
            variant={activeView === 'submit' ? 'default' : 'outline'}
            onClick={() => setActiveView('submit')}
            className="flex items-center space-x-2"
            style={activeView !== 'submit' ? { borderColor: colors.buttonOutline, color: colors.buttonOutlineText } : {}}
          >
            <Send className="h-4 w-4" />
            <span>Ask a Question</span>
          </Button>
          <Button
            variant={activeView === 'browse' ? 'default' : 'outline'}
            onClick={() => setActiveView('browse')}
            className="flex items-center space-x-2"
            style={activeView !== 'browse' ? { borderColor: colors.buttonOutline, color: colors.buttonOutlineText } : {}}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Browse Q&A</span>
          </Button>
          <Button
            variant={activeView === 'archive' ? 'default' : 'outline'}
            onClick={() => setActiveView('archive')}
            className="flex items-center space-x-2"
            style={activeView !== 'archive' ? { borderColor: colors.buttonOutline, color: colors.buttonOutlineText } : {}}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span>Archive</span>
          </Button>
        </div>

        {/* Submit Question View */}
        {activeView === 'submit' && (
          <Card className="p-6" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>Submit Your Question</h2>
            <p className="mb-6" style={{ color: colors.subtext }}>
              Get expert advice from our instructors. Your question will be reviewed and answered by a qualified professional.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Display Name */}
              <div>
                <Label htmlFor="displayName" style={{ color: colors.heading }}>Display Name (Optional)</Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Anonymous"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-1"
                  style={{ backgroundColor: isDarkMode ? '#1e3a5f' : undefined, color: isDarkMode ? '#ffffff' : undefined, borderColor: isDarkMode ? '#1e3a5f' : undefined }}
                />
                <p className="text-sm mt-1" style={{ color: colors.subtext }}>
                  Leave blank to post anonymously
                </p>
              </div>

              {/* Question Text */}
              <div>
                <Label htmlFor="questionText" style={{ color: colors.heading }}>Your Question *</Label>
                <Textarea
                  id="questionText"
                  placeholder="Describe your case or question in detail (minimum 10 characters)..."
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  rows={8}
                  className="mt-1"
                  required
                  style={{ backgroundColor: isDarkMode ? '#1e3a5f' : undefined, color: isDarkMode ? '#ffffff' : undefined, borderColor: isDarkMode ? '#1e3a5f' : undefined }}
                />
                <p className="text-sm mt-1" style={{ color: colors.subtext }}>
                  {questionText.length} / 10 minimum characters
                </p>
              </div>

              {/* File Upload */}
              <div>
                <Label style={{ color: colors.heading }}>Attachments (Optional)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    id="fileInput"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={files.length >= MAX_FILES}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('fileInput').click()}
                    disabled={files.length >= MAX_FILES}
                    className="w-full sm:w-auto flex items-center space-x-2"
                    style={{ borderColor: isDarkMode ? '#60a5fa' : undefined, color: isDarkMode ? '#ffffff' : undefined }}
                  >
                    <Paperclip className="h-4 w-4" />
                    <span>
                      {files.length >= MAX_FILES
                        ? `Maximum ${MAX_FILES} files reached`
                        : `Add Images/Videos (${files.length}/${MAX_FILES})`}
                    </span>
                  </Button>
                  <p className="text-sm mt-1" style={{ color: colors.subtext }}>
                    Images and videos. Max 500MB per file.
                  </p>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border"
                        style={{ backgroundColor: isDarkMode ? '#f9fafb' : '#f9fafb', borderColor: isDarkMode ? '#e5e7eb' : undefined }}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="ml-2 flex-shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || questionText.length < 10}
                className="w-full sm:w-auto curbside-submit-btn"
                style={{ backgroundColor: '#102862' }}
              >
                {submitting ? (
                  <>
                    <LoadingQuip size="sm" />
                    <span className="ml-2" style={{ color: '#ffffff', fontWeight: 500 }}>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" style={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff', fontWeight: 500 }}>Submit Question</span>
                  </>
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Browse Q&A View */}
        {activeView === 'browse' && (
          <div className="space-y-6">
            {/* View Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold" style={{ color: colors.heading }}>Recent Q&A</h2>
                <p className="text-sm" style={{ color: colors.subtext }}>Showing {displayedQuestions.length} most recent questions</p>
              </div>
              {archivedQuestions.length > 0 && (
                <Button variant="outline" onClick={() => setActiveView('archive')} style={{ borderColor: colors.buttonOutline, color: colors.buttonOutlineText }}>
                  View Archive ({archivedQuestions.length} archived)
                </Button>
              )}
            </div>

            {/* Search and Filter */}
            <Card className="p-4 light-form-card" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Search questions and answers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Tag Filters */}
                {allTags.length > 0 && (
                  <div>
                    <Label className="flex items-center space-x-2 mb-2 filter-tags-label" style={{ color: isDarkMode ? '#ffffff' : colors.heading }}>
                      <Filter className="h-4 w-4" />
                      <span>Filter by Tags</span>
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map(tag => (
                        <Badge
                          key={tag}
                          variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleTag(tag)}
                          style={!selectedTags.includes(tag) ? { borderColor: colors.buttonOutline, color: colors.buttonOutlineText } : {}}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Questions List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingQuip size="lg" />
              </div>
            ) : displayedQuestions.length === 0 ? (
              <Card className="p-12 text-center" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
                <MessageSquare className="h-12 w-12 mx-auto mb-4" style={{ color: colors.subtext }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: colors.heading }}>
                  No Questions Yet
                </h3>
                <p style={{ color: colors.subtext }}>
                  {searchTerm || selectedTags.length > 0
                    ? "No questions match your search criteria"
                    : "Be the first to ask a question!"}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {displayedQuestions.map((question) => (
                  <Card
                    key={question.id}
                    className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                    style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
                    onClick={() => viewQuestionDetail(question.id)}
                  >
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-semibold" style={{ color: colors.heading }}>
                              {question.student_name}
                            </span>
                            <span className="text-sm" style={{ color: colors.subtext }}>
                              • {formatDate(question.created_at)}
                            </span>
                            <span className="text-sm" style={{ color: colors.subtext }}>
                              • {question.view_count} views
                            </span>
                          </div>
                          <p className="line-clamp-2" style={{ color: isDarkMode ? '#e2e8f0' : '#374151' }}>
                            {question.question_text}
                          </p>
                        </div>
                      </div>

                      {/* Tags */}
                      {question.tags && question.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {question.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : undefined, color: isDarkMode ? '#e2e8f0' : undefined }}>
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Media Thumbnails */}
                      {question.attachments && question.attachments.length > 0 && (
                        <div className="mt-3 flex gap-2">
                          {question.attachments.slice(0, 3).map((attachment, idx) => (
                            <MediaThumbnail
                              key={idx}
                              attachment={attachment}
                              getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                            />
                          ))}
                          {question.attachments.length > 3 && (
                            <div className="w-16 h-16 rounded border flex items-center justify-center text-xs font-medium" style={{ borderColor: colors.border, backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6', color: colors.subtext }}>
                              +{question.attachments.length - 3}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Response Preview */}
                      {question.admin_response_text && (
                        <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? '#0f2744' : '#eff6ff', border: isDarkMode ? '1px solid #1e3a5f' : 'none' }}>
                          <p className="text-sm font-semibold mb-1" style={{ color: isDarkMode ? '#60a5fa' : '#1e3a8a' }}>
                            Instructor Response:
                          </p>
                          <p className="text-sm line-clamp-2" style={{ color: isDarkMode ? '#ffffff' : '#1e40af' }}>
                            {question.admin_response_text}
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Archive View */}
        {activeView === 'archive' && (
          <div className="space-y-6">
            {/* View Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold" style={{ color: colors.heading }}>Archive</h2>
                <p className="text-sm" style={{ color: colors.subtext }}>
                  {displayedQuestions.length} archived {displayedQuestions.length === 1 ? 'question' : 'questions'}
                </p>
              </div>
              <Button variant="outline" onClick={() => setActiveView('browse')} style={{ borderColor: colors.buttonOutline, color: colors.buttonOutlineText }}>
                Back to Recent
              </Button>
            </div>

            {/* Search and Filter */}
            <Card className="p-4 light-form-card" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Search questions and answers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Tag Filters */}
                {allTags.length > 0 && (
                  <div>
                    <Label className="flex items-center space-x-2 mb-2 filter-tags-label" style={{ color: isDarkMode ? '#ffffff' : colors.heading }}>
                      <Filter className="h-4 w-4" />
                      <span>Filter by Tags</span>
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map(tag => (
                        <Badge
                          key={tag}
                          variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleTag(tag)}
                          style={!selectedTags.includes(tag) ? { borderColor: colors.buttonOutline, color: colors.buttonOutlineText } : {}}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Questions List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingQuip size="lg" />
              </div>
            ) : displayedQuestions.length === 0 ? (
              <Card className="p-12 text-center" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
                <MessageSquare className="h-12 w-12 mx-auto mb-4" style={{ color: colors.subtext }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: colors.heading }}>
                  No Archived Questions
                </h3>
                <p style={{ color: colors.subtext }}>
                  Questions moved to archive status by instructors will appear here.
                </p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {displayedQuestions.map((question) => (
                  <Card
                    key={question.id}
                    className="p-6 hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-orange-500"
                    style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
                    onClick={() => viewQuestionDetail(question.id)}
                  >
                    <div>
                      {/* Question Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 text-sm mb-2" style={{ color: colors.subtext }}>
                            <span className="font-semibold" style={{ color: colors.heading }}>{question.student_name}</span>
                            <span>•</span>
                            <span>{formatDate(question.created_at)}</span>
                            <span>•</span>
                            <span className="flex items-center">
                              <Eye className="h-3 w-3 mr-1" />
                              {question.view_count}
                            </span>
                          </div>
                          <p className="text-base line-clamp-2 font-medium" style={{ color: colors.heading }}>
                            {question.question_text}
                          </p>
                        </div>
                      </div>

                      {/* Tags & Media Indicators */}
                      {(question.tags?.length > 0 || question.attachments?.length > 0 || question.admin_response_video || question.admin_response_attachments?.length > 0) && (
                        <div className="flex items-center flex-wrap gap-2 mt-3">
                          {question.tags?.map(tag => (
                            <Badge key={tag} variant="secondary" className="text-xs" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : undefined, color: isDarkMode ? '#e2e8f0' : undefined }}>
                              {tag}
                            </Badge>
                          ))}
                          
                          {/* Student Attachments */}
                          {question.attachments?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Paperclip className="h-3 w-3 mr-1" />
                              {question.attachments.length} attachment{question.attachments.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                          
                          {/* Admin Media */}
                          {question.admin_response_video && (
                            <Badge variant="outline" className="text-xs" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#eff6ff', color: isDarkMode ? '#60a5fa' : '#1e40af', borderColor: isDarkMode ? '#3b82f6' : '#bfdbfe' }}>
                              🎥 Video response
                            </Badge>
                          )}
                          {question.admin_response_attachments?.length > 0 && (
                            <Badge variant="outline" className="text-xs" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#eff6ff', color: isDarkMode ? '#60a5fa' : '#1e40af', borderColor: isDarkMode ? '#3b82f6' : '#bfdbfe' }}>
                              📎 {question.admin_response_attachments.length} file{question.admin_response_attachments.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Response Preview */}
                      {question.admin_response_text && (
                        <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? '#0f2744' : '#eff6ff', border: isDarkMode ? '1px solid #1e3a5f' : 'none' }}>
                          <p className="text-sm font-semibold mb-1" style={{ color: isDarkMode ? '#60a5fa' : '#1e3a8a' }}>
                            Instructor Response:
                          </p>
                          <p className="text-sm line-clamp-2" style={{ color: isDarkMode ? '#ffffff' : '#1e40af' }}>
                            {question.admin_response_text}
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Question Detail Modal */}
      <Dialog open={!!selectedQuestion} onOpenChange={() => setSelectedQuestion(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: colors.cardBg }}>
          {selectedQuestion && (
            <div className="space-y-6">
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ color: colors.heading }}>Question Details</DialogTitle>
              </DialogHeader>

              {/* Question Info */}
              <div>
                <div className="flex items-center space-x-2 text-sm mb-3" style={{ color: colors.subtext }}>
                  <span className="font-semibold" style={{ color: colors.heading }}>{selectedQuestion.student_name}</span>
                  <span>•</span>
                  <span>{formatDate(selectedQuestion.created_at)}</span>
                  <span>•</span>
                  <span>{selectedQuestion.view_count} views</span>
                </div>
                
                <p className="whitespace-pre-wrap" style={{ color: colors.heading }}>
                  {selectedQuestion.question_text}
                </p>

                {/* Question Attachments */}
                {selectedQuestion.attachments && selectedQuestion.attachments.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2 block" style={{ color: colors.heading }}>Attachments:</Label>
                    <div className="space-y-3">
                      {selectedQuestion.attachments.map((attachment, idx) => (
                        <FilePreviewCard
                          key={idx}
                          attachment={attachment}
                          getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                          downloadFile={downloadFile}
                          formatFileSize={formatFileSize}
                          isDarkMode={isDarkMode}
                          colors={colors}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedQuestion.tags && selectedQuestion.tags.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {selectedQuestion.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : undefined, color: isDarkMode ? '#e2e8f0' : undefined }}>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Answer */}
              {selectedQuestion.admin_response_text && (
                <div className="border-t pt-6" style={{ borderColor: colors.border }}>
                  <h3 className="text-lg font-semibold mb-3" style={{ color: isDarkMode ? '#93c5fd' : '#1e3a8a' }}>
                    Instructor Response
                  </h3>
                  <p className="whitespace-pre-wrap mb-4" style={{ color: colors.heading }}>
                    {selectedQuestion.admin_response_text}
                  </p>

                  {/* Response Video */}
                  {selectedQuestion.admin_response_video && (
                    <div className="mt-4">
                      <Label className="mb-2 block" style={{ color: colors.heading }}>Instructor Video:</Label>
                      <FilePreviewCard
                        attachment={{
                          filename: selectedQuestion.admin_response_video_filename || 'Response Video',
                          file_path: selectedQuestion.admin_response_video,
                          file_type: 'video',
                          file_size: selectedQuestion.admin_response_video_size || 0
                        }}
                        getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                        downloadFile={downloadFile}
                        formatFileSize={formatFileSize}
                        isDarkMode={isDarkMode}
                        colors={colors}
                      />
                    </div>
                  )}

                  {/* Response Attachments */}
                  {selectedQuestion.admin_response_attachments &&
                    selectedQuestion.admin_response_attachments.length > 0 && (
                      <div className="mt-4">
                        <Label className="mb-2 block" style={{ color: colors.heading }}>Instructor Attachments:</Label>
                        <div className="space-y-3">
                          {selectedQuestion.admin_response_attachments.map((attachment, idx) => (
                            <FilePreviewCard
                              key={idx}
                              attachment={attachment}
                              getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                              downloadFile={downloadFile}
                              formatFileSize={formatFileSize}
                              isDarkMode={isDarkMode}
                              colors={colors}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
